import SearchIcon from '@mui/icons-material/Search';
import { Box, Container, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { WO2ToggleSwitch } from 'src/common';
import { Props } from '../container';
import { buildDescriptionSearchPagination } from '../store/slice';
import { CashStatementLine, CashStatementCsvRow } from '../store/types';
import { CashAccountsTable } from './cashAccountsTable';
import PortfolioSelect from '../../common/components/PortfolioSelect';
import AccountSelect from '../../transactions/components/accountSelect';
import DateRangePicker from 'src/common/components/DateRangePicker';
import WO2Button from 'src/common/components/button/Button';
import { PagedRequest } from 'src/store';

export const CashAccounts = (props: Props): JSX.Element => {
  const {
    parameters,
    clientId,
    showAudValues,
    canShowAudValues,
    setPortfolioParameter,
    setAccountParameter,
    setDateParameter,
    setPaginationParameter,
    setShowAudValues,
    fetchCashStatements,
    downloadCashStatements,
    progressCashStatements,
  } = props;

  const [initialSearchComplete, setInitialSearchComplete] = useState(false);
  const [searchText, setSearchText] = useState('');

  const handleSearchClick = () => {
    const pagination = buildDescriptionSearchPagination(parameters.pagination, searchText);
    setPaginationParameter(pagination);
    if (!!clientId) {
      fetchCashStatements({
        inputs: { clientId, parameters: { ...parameters, pagination } },
        pagination,
      });
    }
  };

  const fetchHandleSearchClick = useCallback(() => {
    if (!!clientId) {
      fetchCashStatements({
        inputs: { clientId, parameters },
        pagination: parameters.pagination,
      });
    }
  }, [fetchCashStatements, clientId, parameters]);

  useEffect(() => {
    if (parameters.accountIdList.length > 0 && !initialSearchComplete) {
      fetchHandleSearchClick();
      setInitialSearchComplete(true);
    }
  }, [parameters, fetchHandleSearchClick, initialSearchComplete]);

  return (
    <Container style={{ paddingTop: '20px', paddingBottom: '20px' }}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <PortfolioSelect items={props.portfolios} selectedPortfolio={props.parameters.portfolio} setPortfolio={setPortfolioParameter} />
        </Grid>
        <Grid item>
          <Grid container spacing={2} alignItems="center">
            {canShowAudValues && (
              <Grid item>
                <Box display="flex" alignItems="center" gap={1}>
                  <WO2ToggleSwitch onChange={(e) => setShowAudValues(e.target.checked)} checked={showAudValues} color="primary" />
                  <Typography variant="h4">Show AUD values</Typography>
                </Box>
              </Grid>
            )}
            <Grid item>
              <AccountSelect
                items={props.portfolioAccounts}
                selectedAccountId={props.parameters.selectedAccountId}
                setAccount={setAccountParameter}
              />
            </Grid>
            <Grid item>
              <DateRangePicker dateRange={props.parameters.dates} setDateRange={setDateParameter} isDateOnly={false} paddingRight={0} />
            </Grid>
            <Grid item>
              <TextField
                variant="outlined"
                placeholder="Search description..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchClick();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item>
              <WO2Button color={'primary'} disableElevation onClick={handleSearchClick} variant={'contained'} value="Search">
                Search
              </WO2Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ paddingTop: '20px', paddingBottom: '20px' }}>
        <Grid item xs={12}>
          <CashAccountsTable
            pagedRequest={props.parameters.pagination}
            data={props.cashStatements?.results ?? []}
            showAudValues={showAudValues}
            progressCashStatements={progressCashStatements}
            handleDownload={(pagedRequest: PagedRequest, mapper: (lines: CashStatementLine[]) => CashStatementCsvRow[]) => {
              if (!!clientId) {
                downloadCashStatements({
                  inputs: { clientId, parameters },
                  pagination: pagedRequest,
                  mapper,
                });
              }
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};
